import type { ColorScheme, TelegramThemeParams } from "./types";

interface Palette {
  bg: string;
  bottomBar: string;
  brand: string;
  buttonText: string;
  danger: string;
  header: string;
  line: string;
  muted: string;
  section: string;
  surface: string;
  text: string;
}

const FALLBACK: Record<ColorScheme, Palette> = {
  dark: {
    bg: "#0E1623",
    bottomBar: "#182231",
    brand: "#52B8FF",
    buttonText: "#FFFFFF",
    danger: "#FF7E96",
    header: "#0E1623",
    line: "#2D3B4D",
    muted: "#97A7BA",
    section: "#F2F7FC",
    surface: "#182231",
    text: "#F2F7FC",
  },
  light: {
    bg: "#F4F7FB",
    bottomBar: "#FFFFFF",
    brand: "#2EA6FF",
    buttonText: "#FFFFFF",
    danger: "#D84F6A",
    header: "#F4F7FB",
    line: "#D7E1EC",
    muted: "#66758A",
    section: "#102033",
    surface: "#FFFFFF",
    text: "#102033",
  },
};

function expandHex(value: string): string {
  if (value.length === 4) {
    return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`;
  }
  return value;
}

function normalizeHex(value: string | undefined, fallback: string): string {
  if (!value) {
    return fallback;
  }

  const normalized = value.trim();
  if (!/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/iu.test(normalized)) {
    return fallback;
  }

  return expandHex(normalized).toUpperCase();
}

function hexToRgb(value: string): [number, number, number] {
  const normalized = normalizeHex(value, "#000000").slice(1);
  return [
    Number.parseInt(normalized.slice(0, 2), 16),
    Number.parseInt(normalized.slice(2, 4), 16),
    Number.parseInt(normalized.slice(4, 6), 16),
  ];
}

function rgbToHex(red: number, green: number, blue: number): string {
  return `#${[red, green, blue]
    .map((value) => Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, "0"))
    .join("")}`.toUpperCase();
}

function mix(base: string, overlay: string, ratio: number): string {
  const [baseR, baseG, baseB] = hexToRgb(base);
  const [overlayR, overlayG, overlayB] = hexToRgb(overlay);

  return rgbToHex(
    baseR + (overlayR - baseR) * ratio,
    baseG + (overlayG - baseG) * ratio,
    baseB + (overlayB - baseB) * ratio,
  );
}

function colorDistance(first: string, second: string): number {
  const [r1, g1, b1] = hexToRgb(first);
  const [r2, g2, b2] = hexToRgb(second);
  return Math.hypot(r1 - r2, g1 - g2, b1 - b2);
}

export function applyTheme(themeParams: TelegramThemeParams | undefined, colorScheme: ColorScheme): void {
  const fallback = FALLBACK[colorScheme];
  const root = document.documentElement;
  const pageBg = normalizeHex(themeParams?.bg_color, fallback.bg);
  const surface = normalizeHex(themeParams?.secondary_bg_color, fallback.surface);
  const header = normalizeHex(themeParams?.header_bg_color, fallback.header);
  const bottomBar = normalizeHex(themeParams?.bottom_bar_bg_color, fallback.bottomBar);
  const text = normalizeHex(themeParams?.text_color, fallback.text);
  const muted = normalizeHex(themeParams?.subtitle_text_color ?? themeParams?.hint_color, fallback.muted);
  const section = normalizeHex(themeParams?.section_header_text_color, fallback.section);
  const line = normalizeHex(themeParams?.section_separator_color, fallback.line);
  const brand = normalizeHex(themeParams?.button_color ?? themeParams?.link_color, fallback.brand);
  const brandSoft = mix(pageBg, brand, colorScheme === "dark" ? 0.28 : 0.12);
  const surfaceSoft = normalizeHex(themeParams?.section_bg_color, mix(pageBg, surface, 0.62));
  const danger = normalizeHex(themeParams?.destructive_text_color, fallback.danger);
  const success = colorScheme === "dark" ? "#34C79A" : "#1FAD83";
  const successSoft = colorScheme === "dark" ? "#15392F" : "#E7F8F1";
  const warning = colorScheme === "dark" ? "#F0B646" : "#C98A1A";
  const warningSoft = colorScheme === "dark" ? "#473519" : "#FFF4DD";
  const neutralSoft = colorScheme === "dark" ? "#1F2B3B" : "#EEF4FA";
  const lineStrong = colorDistance(pageBg, surface) < 22 ? line : mix(line, text, colorScheme === "dark" ? 0.24 : 0.12);
  const buttonText = normalizeHex(themeParams?.button_text_color, fallback.buttonText);
  const brandPressed = mix(brand, colorScheme === "dark" ? "#000000" : "#102033", colorScheme === "dark" ? 0.16 : 0.1);
  const shadow = colorScheme === "dark" ? "0 6px 18px rgba(0, 0, 0, 0.24)" : "0 10px 30px rgba(16, 32, 51, 0.08)";

  root.dataset.colorScheme = colorScheme;
  root.style.setProperty("color-scheme", colorScheme);
  root.style.setProperty("--page-bg", pageBg);
  root.style.setProperty("--surface-card", surface);
  root.style.setProperty("--surface-soft", surfaceSoft);
  root.style.setProperty("--surface-neutral", neutralSoft);
  root.style.setProperty("--header-bg", header);
  root.style.setProperty("--bottom-bar-bg", bottomBar);
  root.style.setProperty("--text-primary", text);
  root.style.setProperty("--text-secondary", muted);
  root.style.setProperty("--text-section", section);
  root.style.setProperty("--line-color", line);
  root.style.setProperty("--line-strong", lineStrong);
  root.style.setProperty("--brand-color", brand);
  root.style.setProperty("--brand-pressed", brandPressed);
  root.style.setProperty("--brand-soft", brandSoft);
  root.style.setProperty("--button-text", buttonText);
  root.style.setProperty("--success-color", success);
  root.style.setProperty("--success-soft", successSoft);
  root.style.setProperty("--warning-color", warning);
  root.style.setProperty("--warning-soft", warningSoft);
  root.style.setProperty("--danger-color", danger);
  root.style.setProperty("--danger-soft", mix(pageBg, danger, colorScheme === "dark" ? 0.22 : 0.12));
  root.style.setProperty("--shadow-card", shadow);
}
